import React, { useRef, memo, useEffect, useState, useMemo } from "react";
import { Button, Dialog, TextField, Tooltip } from "@mui/material";
import { useVariableTooltip } from "hooks/useVariableTooltip";
import { DialogVariableTooltip } from "./DialogVariableTooltip";
import { ContextMenu } from 'primereact/contextmenu';
import { PlusIcon } from "lucide-react";
import {
  createVariable,
} from "../../redux-store/variableReducers/variableActions";
import { useDispatch, connect } from "react-redux";
import { useSnackbar } from "../../contexts/CustomSnackbarContext";
import { getVariableSet } from "redux-store/variableSetReducers/variableSetActions";
import { setIsSaveRequest } from "redux-store/variableSetReducers/variableSetSlice";

const TextFieldWithVariableTooltip = memo(({
  showContextMenu = true,
  InputProps,
  environmentVariables,
  collectionVariables,
  folderVariables,
  selectedEnvironmentId,
  selectedEnvironmentName,
  apiResponseVariables,
  ...props
}) => {
  const dispatch = useDispatch();
  const { openSnackbar } = useSnackbar();
  const variableMenu = useRef(null);
  const textFieldRef = useRef(null);
  const [latestEnvironmentVariables, setLatestEnvironmentVariables] = useState(environmentVariables);
  const [isMouseOverTooltip, setIsMouseOverTooltip] = useState(false);
  const closeTimeoutRef = useRef(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  useEffect(() => {
    if (environmentVariables && Object.keys(environmentVariables).length > 0) {
      setLatestEnvironmentVariables(environmentVariables);
    }
  }, [environmentVariables]);

  const variableSources = useMemo(() => {
    return {
      environment: environmentVariables,
      collection: collectionVariables,
      folder: folderVariables,
      apiResponseVariables: apiResponseVariables || {}
    };
  }, [latestEnvironmentVariables, collectionVariables, folderVariables, apiResponseVariables]);

  const {
    handleMouseMove,
    dialogOpen,
    dialogPosition,
    selectedVariable,
    handleClose,
    setVariableKey,
    setVariableValue,
    variableKey,
    variableValue,
    isResolvingFunction,
  } = useVariableTooltip(
    textFieldRef,
    variableSources
  );

  const items = [
    {
      label: "Add Variable",
      command: () => handleOpenAddVariableDialog(),
      icon: <PlusIcon size={16} className="mr-3" />
    },
  ];

  const handleContextMenu = (e) => {
    e.preventDefault();
    const selection = window.getSelection().toString().trim();

    if (selection) {
      setSelectedText(selection);
      setTimeout(() => variableMenu.current.show(e), 10);
    }
  };

  const handleOpenAddVariableDialog = () => {
    setAddVariableDialogOpen(true);
  };


  const fetchSelectedEnvironment = async () => {
    if (selectedEnvironmentId) {
      const result = await dispatch(getVariableSet({
        variableSetId: selectedEnvironmentId
      })).unwrap();
    }
  };

  const handleSaveNewVariable = async () => {
    if (!newVariableName.trim()) return;
    const data = {
      resourceId: selectedEnvironmentId,
      resourceType: "VariableSet",
      var_name: newVariableName,
      var_value: selectedText,
    };

    try {
      const response = await dispatch(createVariable(data));
      const { errors = [], variable } = response.payload || {};

      if (errors.length > 0) {
        openSnackbar({ message: errors.join(", "), severity: 'error' });
        return;
      }

      if (variable) {
        openSnackbar({ message: "Variable created successfully", severity: 'success' });

        setLatestEnvironmentVariables(prev => ({
          ...prev,
          [newVariableName]: selectedText,
        }));
        await fetchSelectedEnvironment();

        const updatedValue = props.value.replace(
          selectedText,
          `{{${newVariableName}}}`
        );

        // First update the value
        props.onChange({ target: { value: updatedValue } });
        
        // Wait for a small delay to ensure state is updated
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Then trigger the save request
        dispatch(setIsSaveRequest(true));

        setAddVariableDialogOpen(false);
        setNewVariableName("");
        return;
      }

      openSnackbar({ message: "Something Went Wrong", severity: 'error' });
    } catch (error) {
      console.error("Error saving variable:", error);
    }
  };

  const [addVariableDialogOpen, setAddVariableDialogOpen] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [newVariableName, setNewVariableName] = useState("");

  const handleTextFieldMouseMove = (e) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    // Get the actual input element
    const input = e.target.querySelector('input') || e.target;
    const value = input?.value;
    if (!value) return;

    // Get the correct bounding rect
    const rect = input.getBoundingClientRect();
    const x = e.clientX - rect.left;

    const variableMatches = [...value.matchAll(/{{(.*?)}}/g)];
    let currentVariable = null;
    let isOverVariable = false;

    for (const match of variableMatches) {
      const [fullMatch, extractedVar] = match;
      const startIndex = value.indexOf(fullMatch, match.index);
      const endIndex = startIndex + fullMatch.length;

      const tempSpan = document.createElement('span');
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.whiteSpace = 'pre';
      tempSpan.style.font = window.getComputedStyle(input).font;
      document.body.appendChild(tempSpan);

      tempSpan.textContent = value.substring(0, startIndex);
      const startX = tempSpan.offsetWidth;

      tempSpan.textContent = fullMatch;
      const varWidth = tempSpan.offsetWidth;
      const endX = startX + varWidth;

      document.body.removeChild(tempSpan);

      if (x >= startX - 5 && x <= endX + 5) {
        currentVariable = extractedVar.trim();
        isOverVariable = true;
        break;
      }
    }

    if (currentVariable && isOverVariable) {
      setTooltipVisible(true);
      handleMouseMove({
        clientX: e.clientX,
        clientY: e.clientY,
        variableName: currentVariable,
        event: e,
      });
    } else {
      setTooltipVisible(false);
      handleClose();
    }
  };

  const handleMouseLeave = () => {
    if (!isMouseOverTooltip) {
      closeTimeoutRef.current = setTimeout(() => {
        setTooltipVisible(false);
        handleClose();
      }, 100);
    }
  };

  const handleTooltipMouseEnter = () => {
    setIsMouseOverTooltip(true);
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
  };

  const handleTooltipMouseLeave = () => {
    setIsMouseOverTooltip(false);
    setTooltipVisible(false);
    handleClose();
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <TextField
        {...props}
        inputRef={textFieldRef}
        fullWidth
        InputProps={{
          ...InputProps,
          onMouseMove: (e) => {
            InputProps?.onMouseMove?.(e);
            handleTextFieldMouseMove(e);
          },
          onMouseLeave: (e) => {
            InputProps?.onMouseLeave?.(e);
            handleMouseLeave();
          },
          style: {
            ...InputProps?.style,
          },
        }}
        onContextMenu={handleContextMenu}
      />
      {dialogOpen && (
        <div
          style={{
            position: 'fixed',
            left: dialogPosition.left,
            top: dialogPosition.top,
            zIndex: 9999,
          }}
        >
          <Tooltip
            open={tooltipVisible}
            placement="bottom"
            onMouseEnter={handleTooltipMouseEnter}
            onMouseLeave={handleTooltipMouseLeave}
            PopperProps={{
              sx: {
                '& .MuiTooltip-tooltip': {
                  backgroundColor: "var(--background-primary)",
                  color: 'var(--text-primary)',
                  border: '1px solid var(--borderColor)',
                  borderRadius: '6px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), 0 8px 30px rgba(0, 0, 0, 0.12)',
                  padding: '8px',
                  minWidth: '200px',
                  marginTop: '10px',
                },
                '& .MuiTooltip-arrow': {
                  color: "var(--text-primary)",
                  '&::before': {
                    border: '1px solid var(--borderColor)',
                    backgroundColor: "var(--background-primary)",
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                  },
                },
              },
            }}
            arrow
          >
            <div>
              <DialogVariableTooltip
                selectedVariable={selectedVariable || {}}
                setVariableKey={setVariableKey}
                setVariableValue={setVariableValue}
                environmentName={selectedEnvironmentName}
                variableKey={variableKey || ''}
                variableValue={variableValue || ''}
                isResolvingFunction={isResolvingFunction}
              />
            </div>
          </Tooltip>
        </div>
      )}
      <ContextMenu model={items} ref={variableMenu} breakpoint="767px" className={`contextMenuUniqueClass bgSecondary text-[var(--text-secondary)] ${showContextMenu ? "" : "hidden"}`} />
      <Dialog open={addVariableDialogOpen} onClose={() => setAddVariableDialogOpen(false)}>
        <div className="p-4">
          <div>
            <span className="!text-lg">Enter Variable Name</span>
          </div>
          <TextField
            value={newVariableName}
            onChange={(e) => setNewVariableName(e.target.value)}
            placeholder="Variable Name"
            fullWidth
            className="my-2"
          />
          <TextField
            label="Selected Value"
            value={selectedText}
            placeholder="Variable Name"
            fullWidth
            className="my-2"
            disabled
          />
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSaveNewVariable}
          >
            Add Variable
          </Button>
        </div>
      </Dialog>
    </div>
  );
});

TextFieldWithVariableTooltip.displayName = 'TextFieldWithVariableTooltip';
function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    environmentVariables: state.variableSet.selectedEnvironmentVariablesJSON,
    collectionVariables: state.variableSet.selectedCollectionVariablesJSON,
    folderVariables: state.variableSet.selectedFolderVariablesJSON,
    selectedEnvironmentId: state.variableSet.selectedEnvironmentVariables?.id ?? "",
    selectedEnvironmentName: state.variableSet.selectedEnvironmentVariables?.title,
    apiResponseVariables: state.responseVariables.responseVariables
  }
}
export default connect(mapStateToProps)(TextFieldWithVariableTooltip);